import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function formatTimeAgo(timestamp) {
  if (!timestamp) return '';
  const ts = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const date = new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function Feed() {
  const [posts, setPosts] = useState([]);
  const [caption, setCaption] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [imageMime, setImageMime] = useState('image/jpeg');
  const [posting, setPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  const { companionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchFeed();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const fetchFeed = async () => {
    try {
      const res = await axios.get(`${API}/feed/${companionId}?token=${token}`);
      console.log('feed data:', JSON.stringify(res.data));
      setPosts(res.data.posts || []);
    } catch (err) {
      console.log('fetch error:', err);
    }
    setLoading(false);
  };

  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageMime(file.type || 'image/jpeg');
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImagePreview(ev.target.result);
      const base64 = ev.target.result.split(',')[1];
      setImageBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImagePreview(null);
    setImageBase64(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submitPost = async () => {
    if (!caption.trim() && !imageBase64) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/feed/${companionId}`, {
        token,
        companion_id: companionId,
        caption: caption.trim() || null,
        image_base64: imageBase64 || null,
        image_mime: imageMime,
      });
      await fetchFeed();
      setCaption('');
      setImagePreview(null);
      setImageBase64(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      showToast('posted ♡');
    } catch (err) {
      console.log('post error:', err);
      showToast('something went wrong 🥀');
    }
    setPosting(false);
  };

  const sendReply = async (postId) => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      const res = await axios.post(`${API}/feed/${companionId}/comment/${postId}`, {
        token,
        content: replyText.trim(),
      });
      await fetchFeed();
      setReplyText('');
      setReplyingTo(null);
    } catch (err) {
      console.log('reply error:', err);
      showToast('something went wrong 🥀');
    }
    setSendingReply(false);
  };

  return (
    <div style={styles.page}>

      {/* HEADER */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate(-1)}>←</button>
        <div style={styles.headerCenter}>
          <div style={styles.headerTitle}><span style={{fontStyle:'italic'}}>our</span> feed</div>
          <div style={styles.headerSub}>little moments ♡</div>
        </div>
        <div style={{width:36}} />
      </div>

      {/* COMPOSE */}
      <div style={styles.composeWrap}>
        {imagePreview && (
          <div style={styles.previewWrap}>
            <img src={imagePreview} alt="preview" style={styles.previewImg} />
            <button style={styles.removeBtn} onClick={removeImage}>✕</button>
          </div>
        )}
        <div style={styles.composeRow}>
          <input
            style={styles.composeInput}
            placeholder="share a moment…"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submitPost()}
          />
          <button style={styles.imgBtn} onClick={() => fileInputRef.current?.click()}>🌸</button>
          <button
            style={{...styles.sendBtn, opacity: (!caption.trim() && !imageBase64) || posting ? 0.4 : 1}}
            onClick={submitPost}
            disabled={posting || (!caption.trim() && !imageBase64)}
          >
            {posting ? '…' : '↑'}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImagePick} />
        </div>
      </div>

      {/* FEED */}
      <div style={styles.feed}>

        {loading && <p style={styles.hint}>loading… ♡</p>}
        {!loading && posts.length === 0 && <p style={styles.hint}>nothing here yet — share your first moment ♡</p>}

        {posts.map((post) => (
          <div key={post.id} style={styles.card}>

            {/* top row */}
            <div style={styles.cardTop}>
              <span style={styles.youLabel}>you</span>
              <span style={styles.timeLabel}>{formatTimeAgo(post.created_at)}</span>
            </div>

            {/* image */}
            {post.image_url && (
              <img src={post.image_url} alt="" style={styles.postImg} />
            )}

            {/* caption */}
            {post.caption && (
              <p style={styles.caption}>{post.caption}</p>
            )}

            {/* all comments in order */}
            {post.comments && post.comments.filter((c, i) => !(c.role === 'user' && i === 0)).map((c, i) => (
              c.role === 'assistant' ? (
                <div key={c.id || i} style={styles.hisComment}>
                  <div style={styles.accent} />
                  <p style={styles.hisText}>{c.content}</p>
                </div>
              ) : (
                <div key={c.id || i} style={styles.yourComment}>
                  <p style={styles.yourText}>{c.content}</p>
                </div>
              )
            ))}

            {/* reply input */}
            {replyingTo === post.id ? (
              <div style={styles.replyRow}>
                <input
                  style={styles.replyInput}
                  placeholder="say something…"
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendReply(post.id)}
                  autoFocus
                />
                <button
                  style={{...styles.replySend, opacity: !replyText.trim() || sendingReply ? 0.4 : 1}}
                  onClick={() => sendReply(post.id)}
                  disabled={!replyText.trim() || sendingReply}
                >
                  {sendingReply ? '…' : '↑'}
                </button>
                <button style={styles.replyCancel} onClick={() => { setReplyingTo(null); setReplyText(''); }}>✕</button>
              </div>
            ) : (
              <button style={styles.replyBtn} onClick={() => setReplyingTo(post.id)}>reply ♡</button>
            )}

          </div>
        ))}
      </div>

      {toast && <div style={styles.toast}>{toast}</div>}
    </div>
  );
}

const styles = {
  page: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif",
    display: 'flex', flexDirection: 'column', overflowY: 'hidden',
  },
  header: {
    padding: '24px 28px 20px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
    borderBottom: '1px solid rgba(201,147,90,0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexShrink: 0,
  },
  backBtn: {
    width: 36, height: 36, borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#c9935a', fontSize: 16, cursor: 'pointer',
  },
  headerCenter: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  headerTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 22, color: '#e8ddd0', fontWeight: 300,
  },
  headerSub: { fontSize: 11, color: '#4a3d30', fontWeight: 300, marginTop: 2 },
  composeWrap: {
    margin: '16px 20px',
    background: '#1a1520', borderRadius: 20,
    border: '1px solid rgba(201,147,90,0.1)',
    flexShrink: 0,
  },
  previewWrap: { position: 'relative' },
  previewImg: { width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: '20px 20px 0 0', display: 'block' },
  removeBtn: {
    position: 'absolute', top: 8, right: 8,
    width: 24, height: 24, borderRadius: '50%',
    background: 'rgba(14,11,15,0.8)', border: 'none',
    color: '#c9935a', fontSize: 11, cursor: 'pointer',
  },
  composeRow: {
    display: 'flex', alignItems: 'center',
    padding: '8px 8px 8px 16px', gap: 8,
  },
  composeInput: {
    flex: 1, border: 'none', background: 'transparent',
    fontFamily: "'DM Sans', sans-serif", fontSize: 15,
    color: '#e8ddd0', outline: 'none',
  },
  imgBtn: {
    width: 36, height: 36, borderRadius: '50%',
    border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer',
  },
  sendBtn: {
    width: 36, height: 36, borderRadius: '50%', border: 'none',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)',
    color: '#0e0b0f', fontSize: 16, fontWeight: 600, cursor: 'pointer',
  },
  feed: {
    flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
    padding: '0 20px 80px', display: 'flex', flexDirection: 'column', gap: 16,
  },
  hint: { fontSize: 13, color: '#2a1f16', textAlign: 'center', padding: '40px 0' },
  card: {
    background: '#1e1825', borderRadius: 20,
    border: '1px solid rgba(201,147,90,0.08)',
    display: 'flex', flexDirection: 'column',
  },
  cardTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 16px 10px',
  },
  youLabel: { fontSize: 11, color: '#8a5f35', letterSpacing: '0.1em', textTransform: 'uppercase' },
  timeLabel: { fontSize: 11, color: '#3a2a1a' },
  postImg: { width: '100%', height: 'auto', objectFit: 'contain', display: 'block', background: '#0e0b0f' },
  caption: { fontSize: 15, color: '#e8ddd0', fontWeight: 300, lineHeight: 1.6, margin: 0, padding: '12px 16px' },
  hisComment: {
    display: 'flex', gap: 8, alignItems: 'flex-start',
    padding: '12px 16px',
    borderTop: '1px solid rgba(201,147,90,0.06)',
  },
  accent: {
    width: 2, borderRadius: 2, flexShrink: 0,
    background: 'linear-gradient(180deg, #8a5f35, transparent)',
    alignSelf: 'stretch', minHeight: 16,
  },
  hisText: { fontSize: 14, color: '#c8bfb0', fontWeight: 300, lineHeight: 1.65, fontStyle: 'italic', margin: 0, flex: 1 },
  yourComment: {
    display: 'flex', justifyContent: 'flex-end',
    padding: '8px 16px',
  },
  yourText: {
    fontSize: 14, color: '#e8ddd0', fontWeight: 300, lineHeight: 1.65,
    background: '#2a1f16', borderRadius: 12, padding: '8px 12px',
    border: '1px solid rgba(201,147,90,0.08)', margin: 0,
  },
  replyRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 12px',
    borderTop: '1px solid rgba(201,147,90,0.06)',
  },
  replyInput: {
    flex: 1, border: 'none', background: 'transparent',
    fontFamily: "'DM Sans', sans-serif", fontSize: 14,
    color: '#e8ddd0', outline: 'none',
  },
  replySend: {
    width: 30, height: 30, borderRadius: '50%', border: 'none',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)',
    color: '#0e0b0f', fontSize: 14, cursor: 'pointer',
  },
  replyCancel: {
    width: 28, height: 28, borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#4a3d30', fontSize: 11, cursor: 'pointer',
  },
  replyBtn: {
    background: 'transparent', border: 'none',
    color: '#3a2a1a', fontSize: 12, fontWeight: 300,
    cursor: 'pointer', padding: '10px 16px',
    borderTop: '1px solid rgba(201,147,90,0.04)',
    textAlign: 'left', fontFamily: "'DM Sans', sans-serif",
  },
  toast: {
    position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
    background: 'rgba(201,147,90,0.15)', border: '1px solid rgba(201,147,90,0.3)',
    color: '#c9935a', padding: '10px 24px', borderRadius: 20, fontSize: 13,
    whiteSpace: 'nowrap', zIndex: 100,
  },
};

export default Feed;
